import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteProps, createFilterOptions, FilterOptionsState, TextFieldProps} from "@mui/material";
import {CancelToken} from "axios";

export interface SalesTaxOption {
    code: string;
    name?: string;
    rate?: number;
    isProvincial?: boolean;
}

interface SalesTaxAutocompleteProps<M extends boolean = false> extends Omit<AutocompleteProps<SalesTaxOption, M, false, false>, 'renderInput' | 'renderOption' | 'options'> {
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;

    [key: string]: any;
}

const SalesTaxAutocomplete = <M extends boolean = false, >({
                                                               textFieldProps = {},
                                                               form = false,
                                                               name = undefined,
                                                               required = false,
                                                               setLoadingOptions: setLoadingOptionsProps,
                                                               ...props
                                                           }: SalesTaxAutocompleteProps<M>) => {
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);

    /**
     * Syncs the internal loading state with the parent component
     */
    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    /**
     * Fetches part options based on search keywords and industry
     *
     * @param cancelToken - Axios cancel token for request cancellation
     * @returns Array of matching part options
     */
    const getOptions = useCallback(async (cancelToken: CancelToken) => {
        setLoadingOptions(true);
        let list: any[] = [];
        let total: number | undefined = undefined;
        try {
            do {
                // @ts-ignore - API type definition is not complete
                const response = await Api.getSalesTaxes({pagination: {currentPage: 1, pageSize: 50}, query: ""}, cancelToken);
                if (!isMounted())
                    return list;
                if (response?.data?.items) {
                    list = [...list, ...response.data.items];
                    total = response.data.total;
                } else {
                    total = 0; // No items means total is 0
                }
            } while (total !== undefined && list.length < total);
            return list;
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [isMounted]);

    /**
     * Determines if two part options are equal based on their IDs
     */
    const isOptionEqualToValue = useCallback((option: SalesTaxOption | string, value: SalesTaxOption | null | string) =>
        (typeof option === 'string' ? option : option?.code) === (typeof value === 'string' ? value : value?.code), []);

    /**
     * Gets the display label for a part option (part number)
     */
    const getOptionLabel = useCallback((option: SalesTaxOption | string) => typeof option === 'string' ? option : option.name ? `${option.code} - ${option.name}` : option.code, []);

    /**
     * Gets the unique key for a part option
     */
    const getOptionKey = useCallback((option: SalesTaxOption) => typeof option === 'string' ? `freesolo_${option}` : option?.code ?? "", []);

    // GetOptions will populate an initial empty array
    const defaultOptions = useMemo(() => [], []);

    const defaultFilterOptions = useMemo(() => createFilterOptions<SalesTaxOption>(), []);

    /**
     * Custom filter options function that allows for free solo input. If the input value doesn't match any of the existing options, it will be added as a new option to the filtered list.
     */
    const filterOptions = useCallback((options: SalesTaxOption[], state: FilterOptionsState<SalesTaxOption>) => {
        const filtered = defaultFilterOptions(options, state);
        // If the input value doesn't match any of the options, add it as a free solo option
        if (props.freeSolo && !!state.inputValue?.trim() && !options.some(option => getOptionLabel(option).toLowerCase() === state.inputValue.toLowerCase())) {
            filtered.push(state.inputValue as any);
        }
        return filtered;
    }, [defaultFilterOptions, getOptionLabel, props.freeSolo]);

    return (
        <AutocompleteBase<SalesTaxOption, M>
            keywordsBased={false}
            canSearchEmptyKeywords
            getOptions={getOptions}
            timeout={1000}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            options={defaultOptions}
            clearOnBlur={props.freeSolo}
            {...props}
            filterOptions={filterOptions}
        />
    );
};

export default SalesTaxAutocomplete;